/// <amd-module name="Wit/SprintsHub/SprintsView/Services/WorkDetailsService" />

import { calculateRemainingWorkingDays, getTodayInLocalTimeWithNoHours } from "Agile/Common/Utils/CapacityDateUtils";
import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import { IObservableArray, ObservableArray } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IClientWorkItem, IObservableClientWorkItem } from "Wit/ClientWorkItemService/IClientWorkItem";
import { shiftToUTC } from "Wit/Common/DateUtils";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import { convertWorkItemIdentityRefFromFieldValue } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { ISprintCapacityOptions } from "Wit/SprintsHub/Common/Contracts/Interfaces";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { CapacityRevisionActions, ICapacityService, IUserActivity } from "./ICapacityService";
import { ITaskboardService } from "./ITaskboardService";

export interface IGroupedProgressDataChange {
    id: string;
    text: string;
    workItemIdentityRef?: WorkItemIdentityRef;
    current: number;
    total: number;
}

export enum WorkDetailDataProivderTypes {
    Activity = "ActivityGroupDataProvider",
    AssignedTo = "AssignedToGroupDataProvider",
    Team = "TeamGroupDataProvider"
}

export interface IWorkDetailsService extends IVssService {
    loadData(teamSettings: ITeamSettings, selectedIteration: IIterationData): void;
    getDataProvider(type: WorkDetailDataProivderTypes): GroupedProgressDataProvider;
}

class WorkDetailsService extends VssService implements IWorkDetailsService {
    private selectedIteration: IIterationData;
    private teamSettings: ITeamSettings;

    private assignedToDataProvider: AssignedToGroupDataProvider;
    private activityDataProvider: ActivityGroupDataProvider;
    private teamDataProvider: TeamGroupDataProvider;

    private capacityService: ICapacityService;
    private taskboardService: ITaskboardService;

    public _serviceStart(pageContext: IVssPageContext) {
        this.pageContext = pageContext;

        this.capacityService = pageContext.getService<ICapacityService>("ICapacityService");
        this.capacityService.getChangeObservable().subscribe(this.updateDataProviderData);

        this.taskboardService = pageContext.getService<ITaskboardService>("ITaskboardService");
        this.taskboardService.subscribe(this.updateDataProviderData);

        this.assignedToDataProvider = new AssignedToGroupDataProvider(pageContext);
        this.activityDataProvider = new ActivityGroupDataProvider(pageContext);
        this.teamDataProvider = new TeamGroupDataProvider(pageContext);
    }

    public _serviceEnd() {
        this.capacityService.getChangeObservable().unsubscribe(this.updateDataProviderData);
        this.taskboardService.unsubscribe(this.updateDataProviderData);
    }

    public async loadData(teamSettings: ITeamSettings, selectedIteration: IIterationData) {
        if (teamSettings.teamId && teamSettings.projectId) {
            this.teamSettings = teamSettings;
            this.selectedIteration = selectedIteration;
            if (!(await this.capacityService.loadCapacityData(teamSettings.teamId, teamSettings.projectId, selectedIteration.id))) {
                // If data was not updated just update the data providers
                this.updateDataProviderData(0, CapacityRevisionActions.Capacity);
            }
        }
    }

    public getDataProvider(type: WorkDetailDataProivderTypes): GroupedProgressDataProvider {
        switch (type) {
            case WorkDetailDataProivderTypes.Activity:
                return this.activityDataProvider;
            case WorkDetailDataProivderTypes.AssignedTo:
                return this.assignedToDataProvider;
            default:
                return this.teamDataProvider;
        }
    }

    public getActivityDataProvider(): ActivityGroupDataProvider {
        return this.activityDataProvider;
    }

    public getTeamDataProvider(): TeamGroupDataProvider {
        return this.teamDataProvider;
    }

    private updateDataProviderData = (value: any, action?: string) => {
        let dataProviders: GroupedProgressDataProvider[];

        let taskBoardItems = this.taskboardService.getAllItems();
        if (!taskBoardItems.length) {
            this.taskboardService.initializeRows();
            taskBoardItems = this.taskboardService.getAllItems();
        }

        if (!this.selectedIteration) {
            return;
        }

        taskBoardItems = taskBoardItems.filter(
            item => item.value.getFieldValue<string>(CoreFieldRefNames.IterationPath)?.includes(this.selectedIteration.friendlyPath) ?? false
        );

        switch (action) {
            // Only update the activity data provider if thats the change
            case CapacityRevisionActions.Activity:
                dataProviders = [this.activityDataProvider];
                break;
            default:
                dataProviders = [this.assignedToDataProvider, this.activityDataProvider, this.teamDataProvider];
        }

        dataProviders.forEach(dataProvider => dataProvider?.updateData(this.teamSettings, this.selectedIteration, taskBoardItems));
    };
}

const serviceName = "IWorkDetailsService";
Services.add(serviceName, { serviceFactory: WorkDetailsService });

export abstract class GroupedProgressDataProvider {
    private _workItemRollupField: string;

    protected capacityService: ICapacityService;
    protected capacityOptions: ISprintCapacityOptions | undefined;
    protected taskboardService: ITaskboardService;

    protected data = new ObservableArray<IGroupedProgressDataChange>([]);
    protected pageContext: IVssPageContext;

    protected get workItemRollupField() {
        if (!this._workItemRollupField) {
            this._workItemRollupField =
                this.pageContext.getService<ITaskboardService>("ITaskboardService").getTaskBoardData().value?.taskboardModel.workRollupField ?? "";
        }

        return this._workItemRollupField;
    }

    constructor(pageContext: IVssPageContext) {
        this.pageContext = pageContext;
        this.capacityService = pageContext.getService<ICapacityService>("ICapacityService");
        this.taskboardService = pageContext.getService<ITaskboardService>("ITaskboardService");
        this.capacityOptions = this.capacityService.getCapacityOptions();
    }

    /**
     * Gets the initial data for the chart.  The data returned is an array of:
     *   {
     *     id: [ID of the item]
     *     text: [Text to display for the item]
     *     current: [The current progress]
     *     total: [The total available progress]
     *   }
     *
     * @return The list of data to show in the GroupedProgressControl
     */
    public getData(): IObservableArray<IGroupedProgressDataChange> {
        return this.data;
    }

    /**
     * Gets the title of the grouping
     */
    public abstract getTitle(): string;

    public abstract updateData(
        teamSettings: ITeamSettings,
        selectedIteration: IIterationData,
        workItems: IObservableClientWorkItem<IClientWorkItem>[]
    ): void;

    protected calculateRemainingWorkingDays(teamSettings: ITeamSettings, capacity: IUserActivity, selectedIteration: IIterationData): number {
        const currentDate = getTodayInLocalTimeWithNoHours();
        return calculateRemainingWorkingDays(
            currentDate,
            teamSettings?.weekends.days ?? [],
            capacity.daysOff?.value ?? [],
            selectedIteration.startDate && shiftToUTC(selectedIteration.startDate),
            selectedIteration.finishDate && shiftToUTC(selectedIteration.finishDate),
            this.capacityService.getTeamDaysOff()
        );
    }

    protected isTaskBacklogWorkItemType(workItem: IObservableClientWorkItem<IClientWorkItem>): boolean {
        const backlogConfig = this.taskboardService.getBacklogConfiguration();
        const allowedTypes: string[] = backlogConfig?.taskBacklog.workItemTypes || [];
        const workItemType = workItem.value.getFieldValue(CoreFieldRefNames.WorkItemType);

        return allowedTypes.some(type => workItemType === type);
    }

    protected compareByDisplayName = (a: IGroupedProgressDataChange, b: IGroupedProgressDataChange) => {
        if (a.id === Resources.Unassigned) {
            return -1;
        }

        if (b.id === Resources.Unassigned) {
            return 1;
        }

        return localeIgnoreCaseComparer(a.text, b.text);
    }
}

export class AssignedToGroupDataProvider extends GroupedProgressDataProvider {
    public getTitle() {
        return format(Resources.WorkBy, this.capacityOptions?.assignedToFieldDisplayName);
    }

    public updateData(teamSettings: ITeamSettings, selectedIteration: IIterationData, workItems: IObservableClientWorkItem<IClientWorkItem>[]) {
        const data: IGroupedProgressDataChange[] = [];

        const remainingWorkDaysMap = new Map<string, number>();

        workItems.forEach(workItem => {
            if (!this.isTaskBacklogWorkItemType(workItem)) {
                return;
            }

            const remainingWork: number | null | undefined = workItem.value.getFieldValue(this.workItemRollupField);
            if (!remainingWork) {
                return;
            }

            const assignedTo = workItem.value.getFieldValue(CoreFieldRefNames.AssignedTo)
                ? convertWorkItemIdentityRefFromFieldValue(this.pageContext, workItem.value.getFieldValue(CoreFieldRefNames.AssignedTo))
                : undefined;

            const matchingRow = data.find(row => row.id === (assignedTo?.identityRef.id ?? Resources.Unassigned));

            if (matchingRow) {
                matchingRow.current = matchingRow.current + remainingWork;
            } else {
                data.push({
                    id: assignedTo?.identityRef.id ?? Resources.Unassigned,
                    text: assignedTo?.identityRef.displayName ?? Resources.Unassigned,
                    workItemIdentityRef: assignedTo,
                    current: remainingWork,
                    total: 0
                });
            }
        });

        this.capacityService.getMemberCapacities().value.forEach(capacity => {
            const capPerDay = parseFloat(capacity.capacityPerDay.value);
            if (!capacity.teamMember || isNaN(capPerDay)) {
                return;
            }

            let remainingWorkDays: number;
            if (remainingWorkDaysMap.has(capacity.teamMember.id)) {
                remainingWorkDays = remainingWorkDaysMap.get(capacity.teamMember.id)!;
            } else {
                remainingWorkDays = this.calculateRemainingWorkingDays(teamSettings, capacity, selectedIteration);
                remainingWorkDaysMap.set(capacity.teamMember.id, remainingWorkDays);
            }

            const matchingRow = data.find(row => row.id === capacity.teamMember!.id);

            if (matchingRow) {
                matchingRow.total = matchingRow.total + capPerDay * remainingWorkDays;
            } else if (capPerDay !== 0) {
                data.push({
                    id: capacity.teamMember?.id ?? Resources.Unassigned,
                    text: capacity.teamMember?.displayName ?? Resources.Unassigned,
                    workItemIdentityRef: { distinctDisplayName: capacity.teamMember.uniqueName, identityRef: capacity.teamMember },
                    current: 0,
                    total: capPerDay * remainingWorkDays
                });
            }
        });

        this.data.value = data.sort(this.compareByDisplayName);
    }
}

export class ActivityGroupDataProvider extends GroupedProgressDataProvider {
    public getTitle() {
        return format(Resources.WorkBy, this.capacityOptions?.activityFieldDisplayName);
    }

    public updateData(teamSettings: ITeamSettings, selectedIteration: IIterationData, workItems: IObservableClientWorkItem<IClientWorkItem>[]) {
        const data: IGroupedProgressDataChange[] = [];

        const remainingWorkDaysMap = new Map<string, number>();

        const activityFieldReferenceName = this.capacityOptions?.activityFieldReferenceName ?? "Microsoft.VSTS.Common.Activity";

        workItems.forEach(workItem => {
            if (!this.isTaskBacklogWorkItemType(workItem)) {
                return;
            }

            const remainingWork: number | null | undefined = workItem.value.getFieldValue(this.workItemRollupField);
            const activity: string = workItem.value.getFieldValue(activityFieldReferenceName) ?? Resources.Unassigned;
            if (!remainingWork) {
                return;
            }

            const matchingRow = data.find(row => row.id === activity);

            if (matchingRow) {
                matchingRow.current = matchingRow.current + remainingWork;
            } else {
                data.push({
                    id: activity,
                    text: activity,
                    current: remainingWork,
                    total: 0
                });
            }
        });

        this.capacityService.getMemberCapacities().value.forEach(capacity => {
            const capPerDay = parseFloat(capacity.capacityPerDay.value);
            if (!capacity.teamMember || isNaN(capPerDay)) {
                return;
            }

            let remainingWorkDays: number;
            if (remainingWorkDaysMap.has(capacity.teamMember.id)) {
                remainingWorkDays = remainingWorkDaysMap.get(capacity.teamMember.id)!;
            } else {
                remainingWorkDays = this.calculateRemainingWorkingDays(teamSettings, capacity, selectedIteration);
                remainingWorkDaysMap.set(capacity.teamMember.id, remainingWorkDays);
            }

            const matchingRow = data.find(row => row.id === (capacity.activityName.value || Resources.Unassigned));

            if (matchingRow) {
                matchingRow.total = matchingRow.total + capPerDay * remainingWorkDays;
            } else if (capPerDay !== 0) {
                const idAndText = capacity.activityName.value || Resources.Unassigned;
                data.push({
                    id: idAndText,
                    text: idAndText,
                    current: 0,
                    total: capPerDay * remainingWorkDays
                });
            }
        });

        this.data.value = data.sort(this.compareByDisplayName);
    }
}

export class TeamGroupDataProvider extends GroupedProgressDataProvider {
    public getTitle() {
        return Resources.Work;
    }

    public updateData(teamSettings: ITeamSettings, selectedIteration: IIterationData, workItems: IObservableClientWorkItem<IClientWorkItem>[]) {
        const data: IGroupedProgressDataChange[] = [];

        const remainingWorkDaysMap = new Map<string, number>();

        const totalCapacityPerDay = this.capacityService.getMemberCapacities().value.reduce((sumCap, capacity) => {
            const capPerDay = parseFloat(capacity.capacityPerDay.value);

            if (!capacity.teamMember || isNaN(capPerDay)) {
                return sumCap;
            }

            let remainingWorkDays: number;
            if (capacity.teamMember && remainingWorkDaysMap.has(capacity.teamMember.id)) {
                remainingWorkDays = remainingWorkDaysMap.get(capacity.teamMember.id)!;
            } else {
                remainingWorkDays = this.calculateRemainingWorkingDays(teamSettings, capacity, selectedIteration);
                capacity.teamMember && remainingWorkDaysMap.set(capacity.teamMember.id, remainingWorkDays);
            }

            return sumCap + capPerDay * remainingWorkDays;
        }, 0);

        const totalRemainingWork = workItems.reduce((total, workItem) => {
            if (!this.isTaskBacklogWorkItemType(workItem)) {
                return total;
            }
            const remainingWork: number | null | undefined = workItem.value.getFieldValue(this.workItemRollupField);

            return remainingWork ? remainingWork + total : total;
        }, 0);

        if (totalRemainingWork !== 0 || totalCapacityPerDay !== 0) {
            data.push({
                id: "team-progress",
                text: "Team",
                total: totalCapacityPerDay,
                current: totalRemainingWork
            });
        }

        this.data.value = data;
    }
}
